import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "ress";
import "./App.scss";

import { ProtectedRoute } from "~/components/auth/ProtectedRoute";
import CustomizeSpecificationDetail from "~/components/Document/customizeSpecificationDetail";
import GenerationHistory from "~/components/History/generationHistory";
import GenerationHistoryDetail from "~/components/History/generationHistoryDetail";
import Header from "~/components/layout/header";
import Sidebar from "~/components/layout/sidebar";
import KnowledgeDetail from "~/components/Project/knowledge/KnowledgeDetail";
import SummaryContent from "~/components/Summary/summaryContent";
import TourController from "~/components/Tutorial/TourController";
import UserSettings from "~/components/User/userSettingDisplay";
import type { DocumentType } from "~/config/documents";
import AppDesign from "~/pages/appDesign";
import AppGeneration from "~/pages/appGeneration";
import AppGenerationDetail from "~/pages/appGenerationDetail";
import Billings from "~/pages/billings";
import CostEffectiveness from "~/pages/CostEffectiveness";
import CreditPurchasePage from "~/pages/creditPurchase";
import CustomizeSpecification from "~/pages/customizeSpecification";
import EntityRelationship from "~/pages/entityRelationship";
import Estimation from "~/pages/estimation";
import { ValidationProvider } from "~/pages/estimation/estimationValidation";
import FieldDesign from "~/pages/fieldDesign";
import FuncRequirement from "~/pages/funcRequirement";
import { GenerateStatus } from "~/pages/generateStatus";
import Home from "~/pages/home";
import NonFuncRequirement from "~/pages/nonFuncRequirement";
import NotFound from "~/pages/notFound";
import OrganizationTop from "~/pages/organizationTop";
import PlanChangePage from "~/pages/planChange";
import Plugin from "~/pages/plugin";
import ProjectTop from "~/pages/projectTop";
import ReverseEngineering from "~/pages/reverseEngineering";
import Signin from "~/pages/signin";
import SignUp from "~/pages/signup";
import SignupInvitation from "~/pages/signupInvitation";
import Summary from "~/pages/summary";
import SystemArchitecture from "~/pages/systemArchitecture";
import SystemProposal from "~/pages/systemProposal";
import TutorialAIChat from "~/pages/tutorial/ai-chat";
import TutorialDashboard from "~/pages/tutorial/dashboard";
import TutorialKnowledge from "~/pages/tutorial/knowledge";
import TutorialProjectTop from "~/pages/tutorial/project-top";
import TutorialSummary from "~/pages/tutorial/summary";
import TutorialSystemProposal from "~/pages/tutorial/system-proposal";
import WorkFlow from "~/pages/workFlow";

import { setCurrentProjectOutputsDocumentType, setTourStep } from "~/slices/auth";
import type { RootState } from "~/store";
import { onTutorialTourStart, TUTORIAL_ACTIVE_FLAG } from "~/utils/tutorialEvents";
import { WebPubSubProvider } from "./features/projectTop/webPubSubContext";
import ReverseEngineeringDetail from "./pages/reverseEngineering/detail";

// import { useValidateUrl } from "~/utils/validateUrl";

const RootRedirect = () => {
  const navigate = useNavigate();
  const { currentUser } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // 認証済みユーザーの場合はホームページにリダイレクト
    if (currentUser?.organization_id) {
      navigate(`/${currentUser.organization_id}/home`, { replace: true });
    } else {
      // 未認証の場合はサインインページにリダイレクト
      navigate("/signin", { replace: true });
    }
  }, [navigate, currentUser]);

  return null;
};

// Layoutコンポーネント
const Layout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isTourOpen, setIsTourOpen] = useState(false);
  // const { validate, isLoading } = useValidateUrl();

  // useEffect(() => {
  //   validate();
  // }, []);

  const { currentProject } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    window.dispatchGenerationCompleted = (data) => {
      console.log("[App.tsx] window.dispatchGenerationCompleted called:", {
        source: "service-worker",
        currentProjectId: currentProject?.id,
        incomingProjectId: data.projectId,
        documentType: data.documentType,
        documentId: data.documentId,
        isGenerating: data.isGenerating,
      });

      if (!data.projectId) {
        console.warn("[App.tsx] Ignoring message: projectId is missing");
        return;
      }

      if (data.projectId !== currentProject?.id) {
        console.log(
          `[App.tsx] Ignoring message for different project: ${data.projectId} (current: ${currentProject?.id})`,
        );
        return;
      }

      console.log("[App.tsx] Processing message - dispatching to Redux");
      dispatch(
        setCurrentProjectOutputsDocumentType({
          documentType: data.documentType as DocumentType,
          documentId: data.documentId,
          isGenerating: data.isGenerating,
        }),
      );
    };

    return () => {
      delete window.dispatchGenerationCompleted;
    };
  }, [dispatch, currentProject?.id]);

  useEffect(() => {
    const cleanup = onTutorialTourStart(() => {
      dispatch(setTourStep(0));
      navigate("/tutorial/project-top");
      setIsTourOpen(true);
    });
    return cleanup;
  }, [dispatch, navigate]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isTutorialRoute = location.pathname.startsWith("/tutorial");
    const isActive = localStorage.getItem(TUTORIAL_ACTIVE_FLAG) === "true";

    if (isTutorialRoute && isActive && !isTourOpen) {
      dispatch(setTourStep(0));
      setIsTourOpen(true);
    }
  }, [location.pathname, isTourOpen, dispatch]);

  // tutorialページ以外ではツアーを閉じる
  useEffect(() => {
    if (!location.pathname.startsWith("/tutorial") && isTourOpen) {
      setIsTourOpen(false);
    }
  }, [location.pathname, isTourOpen]);

  const handleCloseTour = () => {
    setIsTourOpen(false);
  };

  return (
    <div className="app">
      <Header />
      <Sidebar />
      <div className="inner">
        <Outlet />
      </div>
      <TourController isOpen={isTourOpen} onClose={handleCloseTour} />
    </div>
  );
};

// ルートの定義
const router = createBrowserRouter([
  {
    path: "/",
    children: [
      { index: true, element: <RootRedirect /> },
      { path: "signin", element: <Signin /> },
      { path: "signup", element: <SignUp /> },
      { path: "signup-invitation", element: <SignupInvitation /> },
      {
        path: "/",
        element: (
          <WebPubSubProvider>
            <Layout />
          </WebPubSubProvider>
        ),
        children: [
          {
            path: "tutorial",
            children: [
              {
                path: "project-top",
                element: <TutorialProjectTop />,
              },
              {
                path: "dashboard",
                element: <TutorialDashboard />,
              },
              {
                path: "summary",
                element: <TutorialSummary />,
              },
              {
                path: "knowledge",
                element: <TutorialKnowledge />,
              },
              {
                path: "system-proposal",
                element: <TutorialSystemProposal />,
              },
              {
                path: "ai-chat",
                element: <TutorialAIChat />,
              },
            ],
          },
          {
            path: ":organizationId",
            children: [
              {
                path: "home",
                element: (
                  <ProtectedRoute requiresOrganization={true}>
                    <Home />
                  </ProtectedRoute>
                ),
              },
              {
                path: "organization",
                element: (
                  <ProtectedRoute requiresOrganization={true}>
                    <OrganizationTop />
                  </ProtectedRoute>
                ),
              },
              {
                path: "organization/info",
                element: (
                  <ProtectedRoute requiresOrganization={true}>
                    <OrganizationTop />
                  </ProtectedRoute>
                ),
              },
              {
                path: "organization/members",
                element: (
                  <ProtectedRoute requiresOrganization={true}>
                    <OrganizationTop />
                  </ProtectedRoute>
                ),
              },
              {
                path: "organization/guests",
                element: (
                  <ProtectedRoute requiresOrganization={true}>
                    <OrganizationTop />
                  </ProtectedRoute>
                ),
              },
              {
                path: "billings",
                element: (
                  <ProtectedRoute requiresOrganization={true}>
                    <Billings />
                  </ProtectedRoute>
                ),
              },
              {
                path: "billings/credit-purchase",
                element: (
                  <ProtectedRoute requiresOrganization={true}>
                    <CreditPurchasePage />
                  </ProtectedRoute>
                ),
              },
              {
                path: "billings/plan-change",
                element: (
                  <ProtectedRoute requiresOrganization={true}>
                    <PlanChangePage />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <ProjectTop />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/dashboard",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <ProjectTop />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/generate-status",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerateStatus />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/info",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <ProjectTop />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/members",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <ProjectTop />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/knowledge",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <ProjectTop />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/knowledge/:id",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <KnowledgeDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/settings",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <ProjectTop />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/system-proposal/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <SystemProposal />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/summary",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <Summary />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/functional-requirements/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <FuncRequirement />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/estimation",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <ValidationProvider>
                      <Estimation />
                    </ValidationProvider>
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/estimation/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <ValidationProvider>
                      <Estimation />
                    </ValidationProvider>
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/estimation/history",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistory />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/estimation/history/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistoryDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/cost-effectiveness",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <CostEffectiveness />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/non-functional-requirements/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <NonFuncRequirement />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/workflow/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <WorkFlow />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/plugin-proposal/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <Plugin />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/app-design/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <AppDesign />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/field-design/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <FieldDesign />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/entity-relationship/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <EntityRelationship />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/system-architecture/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <SystemArchitecture />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/customize-specification/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <CustomizeSpecification />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/customize-specification/:selectedId/:rowIndex",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <CustomizeSpecificationDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "user-setting",
                element: (
                  <ProtectedRoute requiresOrganization={true}>
                    <UserSettings />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/summary/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <SummaryContent />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/system-proposal/history",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistory />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/system-proposal/history/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistoryDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/functional-requirements/history",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistory />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/functional-requirements/history/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistoryDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/non-functional-requirements/history",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistory />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/non-functional-requirements/history/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistoryDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/plugin-proposal/history",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistory />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/plugin-proposal/history/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistoryDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/app-design/history",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistory />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/app-design/history/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistoryDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/workflow/history",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistory />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/workflow/history/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistoryDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/entity-relationship/history",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistory />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/entity-relationship/history/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistoryDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/system-architecture/history",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistory />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/system-architecture/history/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistoryDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/field-design/history",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistory />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/field-design/history/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistoryDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/customize-specification/history",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistory />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/customize-specification/history/:selectedId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <GenerationHistoryDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/reverse-engineering",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <ReverseEngineering />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/reverse-engineering/report/:reportId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <ReverseEngineeringDetail />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/app-generation",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <AppGeneration />
                  </ProtectedRoute>
                ),
              },
              {
                path: "project/:projectId/app-generation/:spaceId",
                element: (
                  <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                    <AppGenerationDetail />
                  </ProtectedRoute>
                ),
              },
              { path: "*", element: <NotFound /> },
            ],
          },
          {
            path: "summary",
            element: <TutorialSummary />,
          },
          {
            path: "knowledge",
            element: <TutorialKnowledge />,
          },
          {
            path: "system-proposal",
            element: <TutorialSystemProposal />,
          },
          {
            path: "ai-chat",
            element: <TutorialAIChat />,
          },
        ],
      },
      {
        path: ":organizationId",
        children: [
          {
            path: "home",
            element: (
              <ProtectedRoute requiresOrganization={true}>
                <Home />
              </ProtectedRoute>
            ),
          },
          {
            path: "organization",
            element: (
              <ProtectedRoute requiresOrganization={true}>
                <OrganizationTop />
              </ProtectedRoute>
            ),
          },
          {
            path: "organization/info",
            element: (
              <ProtectedRoute requiresOrganization={true}>
                <OrganizationTop />
              </ProtectedRoute>
            ),
          },
          {
            path: "organization/members",
            element: (
              <ProtectedRoute requiresOrganization={true}>
                <OrganizationTop />
              </ProtectedRoute>
            ),
          },
          {
            path: "organization/guests",
            element: (
              <ProtectedRoute requiresOrganization={true}>
                <OrganizationTop />
              </ProtectedRoute>
            ),
          },
          {
            path: "billings",
            element: (
              <ProtectedRoute requiresOrganization={true}>
                <Billings />
              </ProtectedRoute>
            ),
          },
          {
            path: "billings/credit-purchase",
            element: (
              <ProtectedRoute requiresOrganization={true}>
                <CreditPurchasePage />
              </ProtectedRoute>
            ),
          },
          {
            path: "billings/plan-change",
            element: (
              <ProtectedRoute requiresOrganization={true}>
                <PlanChangePage />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <ProjectTop />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/dashboard",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <ProjectTop />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/info",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <ProjectTop />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/members",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <ProjectTop />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/knowledge",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <ProjectTop />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/knowledge/:id",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <KnowledgeDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/settings",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <ProjectTop />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/system-proposal/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <SystemProposal />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/summary",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <Summary />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/functional-requirements/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <FuncRequirement />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/estimation",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <ValidationProvider>
                  <Estimation />
                </ValidationProvider>
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/estimation/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <ValidationProvider>
                  <Estimation />
                </ValidationProvider>
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/estimation/history",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistory />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/estimation/history/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistoryDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/cost-effectiveness",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <CostEffectiveness />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/non-functional-requirements/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <NonFuncRequirement />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/workflow/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <WorkFlow />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/plugin-proposal/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <Plugin />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/app-design/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <AppDesign />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/field-design/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <FieldDesign />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/entity-relationship/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <EntityRelationship />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/system-architecture/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <SystemArchitecture />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/customize-specification/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <CustomizeSpecification />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/customize-specification/:selectedId/:rowIndex",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <CustomizeSpecificationDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "user-setting",
            element: (
              <ProtectedRoute requiresOrganization={true}>
                <UserSettings />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/summary/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <SummaryContent />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/system-proposal/history",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistory />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/system-proposal/history/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistoryDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/functional-requirements/history",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistory />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/functional-requirements/history/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistoryDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/non-functional-requirements/history",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistory />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/non-functional-requirements/history/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistoryDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/plugin-proposal/history",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistory />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/plugin-proposal/history/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistoryDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/app-design/history",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistory />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/app-design/history/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistoryDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/workflow/history",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistory />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/workflow/history/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistoryDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/entity-relationship/history",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistory />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/entity-relationship/history/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistoryDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/system-architecture/history",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistory />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/system-architecture/history/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistoryDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/field-design/history",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistory />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/field-design/history/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistoryDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/customize-specification/history",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistory />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/customize-specification/history/:selectedId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <GenerationHistoryDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/reverse-engineering",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <ReverseEngineering />
              </ProtectedRoute>
            ),
          },
          {
            path: "project/:projectId/reverse-engineering/report/:reportId",
            element: (
              <ProtectedRoute requiresOrganization={true} requiresProject={true}>
                <ReverseEngineeringDetail />
              </ProtectedRoute>
            ),
          },
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
]);

// Appコンポーネント
function App() {
  useEffect(() => {
    const scriptId = "cognito-forms-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://services.cognitoforms.com/s/91yWh5pX_0-Vc2R3wsl9_Q/forms.js";
      script.async = true;
      document.body.appendChild(script);
    }

    if ("serviceWorker" in navigator) {
      const handleServiceWorkerMessage = (event: MessageEvent) => {
        if (event.data && event.data.type === "DOCUMENT_GENERATION_COMPLETED") {
          const { documentType, documentId, projectId } = event.data;
          if (documentType && documentId) {
            window.dispatchGenerationCompleted?.({
              documentType,
              documentId,
              isGenerating: false,
              projectId,
            });
          }
        }
      };

      navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);

      return () => {
        navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
      };
    }

    return undefined;
  }, []);

  return <RouterProvider router={router} />;
}

export default App;
